import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Connections" list — mirrors KnowledgeBasesPage.jsx's structure (generic-CRUD-backed
 * list + a "New" button gated by the module's write permission, click a row to open the
 * detail page). Credential entry/rotation/testing lives entirely inside
 * ConnectionDetail.jsx — this page only ever sees the model's stripped rows
 * (has_credentials boolean, never ciphertext — models/AppConnection.model.js).
 */
export function ConnectionStatusBadge({ status }) {
  const variant = { ok: 'success', error: 'error', untested: 'secondary', disabled: 'secondary' };
  return <Badge variant={variant[status] || 'secondary'} size="sm">{status || 'untested'}</Badge>;
}

export default function ConnectionsPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [connections, setConnections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [category, setCategory] = useState('');
  const [applicationId, setApplicationId] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return Promise.all([
      fetchApi('crud', { model: 'AppConnection', action: 'list', limit: 200 }),
      fetchApi('crud', { model: 'AppConnectionCategory', action: 'list', limit: 100 }).catch(() => ({ data: [] })),
      fetchApi('applicationsList').then((res) => (Array.isArray(res) ? res : res?.data) || []).catch(() => []),
    ])
      .then(([connRes, catRes, appsRes]) => {
        setConnections((Array.isArray(connRes) ? connRes : connRes?.data) || []);
        setCategories((Array.isArray(catRes) ? catRes : catRes?.data) || []);
        setApps(appsRes);
      })
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const categoryName = useCallback(
    (key) => (categories.find((c) => c.key === key) || {}).name || key,
    [categories],
  );

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      render: (conn) => (
        <div>
          <div className="font-medium text-primary-600">{conn.name}</div>
          <div className="text-secondary-500 text-xs max-w-xs truncate" title={conn.description}>{conn.description}</div>
        </div>
      ),
    },
    { key: 'category_key', label: 'Category', render: (conn) => categoryName(conn.category_key) },
    { key: 'provider', label: 'Provider', render: (conn) => <span className="font-mono text-xs">{conn.provider}</span> },
    { key: 'has_credentials', label: 'Credentials', searchable: false, render: (conn) => <span className="text-xs">{conn.has_credentials ? 'configured' : '—'}</span> },
    { key: 'status', label: 'Status', searchable: false, render: (conn) => <ConnectionStatusBadge status={conn.is_active ? conn.status : 'disabled'} /> },
    {
      key: 'last_tested_at',
      label: 'Last tested',
      searchable: false,
      render: (conn) => <span className="text-xs text-secondary-500">{conn.last_tested_at ? new Date(conn.last_tested_at).toLocaleString() : '—'}</span>,
    },
  ], [categoryName]);

  const categoryOptions = useMemo(
    () => [{ label: 'All categories', value: '' }, ...categories.map((c) => ({ label: c.name || c.key, value: c.key }))],
    [categories],
  );

  const appOptions = useMemo(() => [
    { label: 'All applications', value: '' },
    ...apps.map((a) => ({ label: a.name, value: String(a.id) })),
  ], [apps]);

  const rows = useMemo(() => {
    let filtered = connections;
    if (category) filtered = filtered.filter((c) => c.category_key === category);
    if (applicationId) filtered = filtered.filter((item) => item.application_id == null || item.application_id === Number(applicationId));
    return filtered;
  }, [connections, category, applicationId]);

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Connections"
        subtitle={<>{connections.length} connection{connections.length === 1 ? '' : 's'} — encrypted credentials for external resources, attached to dynamic tools by category and to agents.</>}
        actions={(
          <Can module="connections" action="author">
            <Button size="sm" onClick={() => nav('/app-connection/new')}>New connection</Button>
          </Can>
        )}
      />

      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        error={err}
        emptyText="No connections yet."
        search={{ placeholder: 'Search connections…', keys: ['name', 'description', 'provider', 'category_key'] }}
        toolbar={(
          <>
            <div className="w-48 sm:w-56"><Select options={appOptions} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable /></div>
            <div className="w-52"><Select options={categoryOptions} value={category} onChange={setCategory} placeholder="All categories" searchable /></div>
          </>
        )}
        onRowClick={(conn) => nav(`/app-connection/${conn.id}`)}
        onReload={reload}
      />
    </PageShell>
  );
}
